import axios from "axios";
import { authenticatorApi } from "@/infrastructure/api/authenticatorApi";

export type ProductResponse = {
  id: number;
  negocioId: number;
  categoriaId: number;
  nichoId: number;
  nome: string;
  descricao: string;
  preco: number;
  status: "PUBLICADO" | "RASCUNHO" | "OCULTO";
  categoria: { id: number; nome: string };
  nicho: { id: number; nome: string; descricao: string; categoriaId: number };
  imagens?: { id: string; url: string; alt_text: string; ordem: number; principal: boolean; content_type: string; tamanho_bytes: number; expires_at: string }[];
  palavras_chave: string[];
  updated_at: string;
};

export type UpdateProductRequest = {
  nome: string;
  descricao: string;
  status: ProductResponse["status"];
  categoriaId: number;
  nichoId: number;
  palavras_chave: string[];
  imagens: { imagem_id: string; alt_text: string; ordem: number; principal: boolean }[];
};

export type CreateProductRequest = {
  nome: string;
  descricao: string;
  preco: number;
  status: "PUBLICADO" | "RASCUNHO";
  categoriaId: number;
  nichoId: number;
  palavrasChave: string[];
};

type UploadRequest = {
  imagens: {
    content_type: string;
    tamanho_bytes: number;
    alt_text: string;
    principal: boolean;
  }[];
};

type UploadResponse = {
  uploads: { imagem_id: string; upload_url: string; expires_at: string }[];
};

export type ProductsResponse = {
  produtos_cadastrados: number;
  visiveis_vitrine: number;
  nao_publicados: number;
  produtos: ProductResponse[];
};

function messageFrom(error: unknown, fallback: string) {
  if (!axios.isAxiosError(error))
    return error instanceof Error ? error.message : fallback;
  if (error.response?.status === 401 || error.response?.status === 403)
    return "Sua sessão expirou. Entre novamente.";
  if (!error.response) return "Não foi possível conectar ao servidor.";
  const detail = error.response.data?.detail;
  return typeof detail === "string" && detail ? detail : fallback;
}

async function safeRequest<T>(request: () => Promise<T>, fallback: string) {
  try {
    return await request();
  } catch (error) {
    throw new Error(messageFrom(error, fallback));
  }
}

export function getProducts() {
  return safeRequest(
    async () =>
      (await authenticatorApi.get<ProductsResponse>("/produtos")).data,
    "Não foi possível carregar os produtos.",
  );
}

export function deleteProduct(productId: number) {
  return safeRequest(
    () => authenticatorApi.delete(`/produtos/${productId}`),
    "Não foi possível apagar o produto.",
  );
}

export function updateProductStatus(
  productId: number,
  status: ProductResponse["status"],
) {
  return safeRequest(
    () => authenticatorApi.patch(`/produtos/${productId}/status`, { status }),
    status === "PUBLICADO"
      ? "Não foi possível publicar o produto."
      : "Não foi possível ocultar o produto.",
  );
}

export function createProduct(payload: CreateProductRequest) {
  return safeRequest(
    async () =>
      (await authenticatorApi.post<ProductResponse>("/produtos", payload)).data,
    "Não foi possível cadastrar o produto.",
  );
}

export function getProductById(productId: number) {
  return safeRequest(async () => (await authenticatorApi.get<ProductResponse>(`/produtos/${productId}`)).data, "Não foi possível carregar o produto.");
}

export function updateProduct(productId: number, payload: UpdateProductRequest) {
  return safeRequest(async () => (await authenticatorApi.put<ProductResponse>(`/produtos/${productId}`, payload)).data, "Não foi possível atualizar o produto.");
}

export function deleteProductImages(productId: number, imageIds: string[]) {
  if (!imageIds.length) return Promise.resolve();
  return safeRequest(() => authenticatorApi.delete(`/produtos/${productId}/imagens`, { data: { imagens_ids: imageIds } }), "Não foi possível excluir as imagens removidas.");
}

export function createProductImageUploadUrls(productId: number, payload: UploadRequest) {
  return safeRequest(
    async () => (await authenticatorApi.post<UploadResponse>(`/produtos/${productId}/imagens/upload-urls`, payload)).data,
    "Não foi possível preparar o envio das imagens.",
  );
}

export function confirmProductImages(productId: number, imageIds: string[]) {
  return safeRequest(
    async () => (await authenticatorApi.post<{ imagens: { imagem_id: string; status?: "ATIVA"; erro?: string }[] }>(`/produtos/${productId}/imagens/confirmar`, { imagens_ids: imageIds })).data,
    "Não foi possível confirmar o envio das imagens.",
  );
}

export async function uploadProductImages(
  productId: number,
  images: { file: File; altText: string; principal: boolean }[],
) {
  // Proxy temporário: o BFF e a API enviam o arquivo ao storage sem violar o CSP.
  return Promise.all(
    images.map(async ({ file, altText, principal }) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("alt_text", altText);
      formData.append("principal", String(principal));
      const { data } = await authenticatorApi.post<{ id: string }>(
        `/produtos/${productId}/imagens/proxy-upload`,
        formData,
        {
          // Sobrescreve o application/json padrão; o Axios inclui o boundary.
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      return data.id;
    }),
  );

  /*
   * ROLLBACK: versão anterior, baseada em URLs assinadas:
   * const results = await Promise.allSettled(
   *   uploads.map((upload, index) =>
   *     fetch(upload.upload_url, {
   *       method: "PUT",
   *       headers: {
   *         "Content-Type": files[index].type,
   *         "ngrok-skip-browser-warning": "true",
   *       },
   *       body: files[index],
   *     }).then((response) => {
   *       if (!response.ok) throw new Error(`Falha no upload: ${response.status}`);
   *       return upload.imagem_id;
   *     }),
   *   ),
   * );
   * const failed = results.find((result) => result.status === "rejected");
   * if (failed) throw new Error("Falha ao enviar uma ou mais imagens.");
   * return results.map((result) =>
   *   (result as PromiseFulfilledResult<string>).value
   * );
   */
}
